import { useEffect, useState } from "react";
import type { Column } from "../types/component";
import type { Row, UseTableProps } from "../types/hooks";
import { type RbRowSelectionModel } from "@scmlatam/web-ui";
import dayjs from "dayjs";
import ExecuteStatusActiveIcon from "../../../../assets/icons/execute-status-active-icon.svg";
import ExecuteStatusInactiveIcon from "../../../../assets/icons/execute-status-inactive-icon.svg";

const useTable = ({
  t,
  employees,
  setLoading,
  period,
  filter,
}: UseTableProps) => {
  const columns: Column[] = [
    {
      key: "personal_number",
      header: t("step3.table.columns.person_number"),
      width: "10vw",
    },
    {
      key: "full_name",
      header: t("step3.table.columns.full_name"),
      width: "20vw",
    },
    {
      key: "orgpath",
      header: t("step3.table.columns.orgpath"),
      width: "35vw",
    },
    {
      key: "status",
      header: t("step3.table.columns.status.label"),
      width: "5vw",
      render: (row: Row) => {
        return (
          <>
            <div className="flex items-center gap-2">
              {row.status === t("step3.table.columns.status.active") ? (
                <img
                  src={ExecuteStatusActiveIcon}
                  alt="execute-status-active-icon"
                  style={{ width: "16px", height: "16px" }}
                />
              ) : (
                <img
                  src={ExecuteStatusInactiveIcon}
                  alt="execute-status-inactive-icon"
                  style={{ width: "16px", height: "16px" }}
                />
              )}

              <span>{row.status}</span>
            </div>
          </>
        );
      },
    },
  ];

  const [rows, setRows] = useState<Row[]>([]);

  const [rowSelected, setRowSelected] = useState<RbRowSelectionModel>({
    ids: new Set(),
    type: "include",
  });

  const getSelectedEmployeeIds = () => {
    if (Array.isArray(rowSelected)) {
      return rowSelected;
    }

    if (rowSelected instanceof Set) {
      return Array.from(rowSelected);
    }

    return Array.from(rowSelected.ids ?? []);
  };

  const generateRows = () => {
    const temporalRows: Row[] = [];
    const employeeIds = new Set<string>();

    for (const employee of employees) {
      if (employeeIds.has(employee.employee_id)) {
        continue;
      }

      employeeIds.add(employee.employee_id);

      const orgpaths = employee.orgpath.sort((a, b) =>
        dayjs(a.Range.end_date).diff(dayjs(b.Range.end_date)),
      );

      const orgpathInRangeOfSearch = orgpaths.filter(
        (o) =>
          period[0]?.isBefore(dayjs(o?.Range?.end_date)) &&
          dayjs(o?.Range?.start_date).isBefore(period[1]),
      );

      let orgpath = "";

      if (orgpathInRangeOfSearch.length > 0) {
        let lastOrgpath =
          orgpathInRangeOfSearch[orgpathInRangeOfSearch.length - 1];

        orgpath = `${lastOrgpath.Area.map((area) => area.name).join(
          "/",
        )}/${lastOrgpath.Posicion.name}`;
      }

      const row: Row = {
        id: employee.employee_id,
        personal_number: employee.document_employee,
        full_name: `${employee.first_name} ${employee.last_name}`,
        orgpath,
        status: t(
          `step3.table.columns.status.${employee.active ? "active" : "inactive"}`,
        ),
      };

      temporalRows.push(row);
    }

    setRows(temporalRows);

    let searchParams = new URLSearchParams(window.location.search);
    const employeesSelected = searchParams.get("employees") || "";

    setRowSelected(employeesSelected.split(",").map((id) => parseInt(id)));

    setLoading(false);
  };

  const filterValue = filter.trim().toLocaleLowerCase();

  const filteredRows = rows.filter((row) =>
    [row.full_name, row.personal_number, row.orgpath, row.status].some(
      (value) => value.toLocaleLowerCase().includes(filterValue),
    ),
  );

  useEffect(() => {
    generateRows();
  }, [employees]);

  return {
    columns,
    rows: filteredRows,
    setRows,
    rowSelected,
    setRowSelected,
    generateRows,
    getSelectedEmployeeIds,
  };
};

export { useTable };
